import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation, Trans } from 'react-i18next';

const ComingSoonMobileApp: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const mouseRef = useRef({ x: 0, y: 0 });
    const isHoveredRef = useRef(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationFrameId: number;
        let time = 0;

        // Resize handler
        const resizeCanvas = () => {
            const parent = canvas.parentElement;
            if (parent) {
                // High DPI awareness
                const dpr = window.devicePixelRatio || 1;
                canvas.width = parent.clientWidth * dpr;
                canvas.height = parent.clientHeight * dpr;
                ctx.scale(dpr, dpr);
                canvas.style.width = `${parent.clientWidth}px`;
                canvas.style.height = `${parent.clientHeight}px`;
            }
        };

        window.addEventListener('resize', resizeCanvas);
        resizeCanvas();

        const handleMouseMove = (e: MouseEvent) => {
            if (!container) return;
            const rect = container.getBoundingClientRect();
            mouseRef.current = {
                // Invert X for RTL to make it feel natural
                x: ((e.clientX - rect.left) / rect.width - 0.5) * (isRTL ? -1 : 1),
                y: (e.clientY - rect.top) / rect.height - 0.5
            };
        };

        const handleMouseEnter = () => { isHoveredRef.current = true; };
        const handleMouseLeave = () => { isHoveredRef.current = false; mouseRef.current = { x: 0, y: 0 }; };

        container.addEventListener('mousemove', handleMouseMove);
        container.addEventListener('mouseenter', handleMouseEnter);
        container.addEventListener('mouseleave', handleMouseLeave);

        // Animation objects
        const particles: { x: number; y: number; size: number; speed: number; opacity: number; depth: number }[] = [];
        const particleCount = 25;

        // Initialize particles
        for (let i = 0; i < particleCount; i++) {
            particles.push({
                x: Math.random() * (canvas.width / (window.devicePixelRatio || 1)),
                y: Math.random() * (canvas.height / (window.devicePixelRatio || 1)),
                size: Math.random() * 2 + 1,
                speed: Math.random() * 0.3 + 0.05,
                opacity: Math.random() * 0.4 + 0.1,
                depth: Math.random()
            });
        }

        const drawPhone = (centerX: number, centerY: number, scale: number, parallaxX: number, parallaxY: number) => {

            // Apply slight parallax to phone
            const phoneX = centerX + parallaxX * 20;
            const phoneY = centerY + parallaxY * 20;

            ctx.save();
            ctx.translate(phoneX, phoneY);
            ctx.scale(scale, scale);

            // Shadow / Glow behind phone
            ctx.shadowColor = 'rgba(16, 185, 129, 0.4)';
            ctx.shadowBlur = 40;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 20;

            // Phone Frame
            ctx.fillStyle = '#111827';
            ctx.strokeStyle = '#374151';
            ctx.lineWidth = 2;

            // Phone Shape
            ctx.beginPath();
            ctx.roundRect(-70, -140, 140, 280, 28);
            ctx.fill();

            ctx.shadowBlur = 0;
            ctx.stroke();

            // Screen Area (Clipped)
            ctx.save();
            ctx.beginPath();
            ctx.roundRect(-66, -136, 132, 272, 24);
            ctx.clip();

            // Modern Gradient Screen
            const screenGradient = ctx.createLinearGradient(-70, -140, 70, 140);
            screenGradient.addColorStop(0, '#064E3B');
            screenGradient.addColorStop(1, '#065F46');
            ctx.fillStyle = screenGradient;
            ctx.fillRect(-70, -140, 140, 280);

            // Abstract UI
            // Header
            ctx.fillStyle = 'rgba(16, 185, 129, 0.2)';
            ctx.roundRect(-66, -136, 132, 50, 0);
            ctx.fill();

            // Avatar
            ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
            ctx.beginPath();
            ctx.arc(-40, -110, 12, 0, Math.PI * 2);
            ctx.fill();

            // Lines
            ctx.roundRect(-20, -116, 60, 6, 3);
            ctx.roundRect(-20, -104, 40, 6, 3);
            ctx.fill();

            // Cards
            const cardYStart = -60;
            for (let i = 0; i < 3; i++) {
                const yPos = cardYStart + (i * 55);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
                ctx.beginPath();
                ctx.roundRect(-55, yPos, 110, 45, 10);
                ctx.fill();

                // Icon inside card
                ctx.fillStyle = i === 0 ? 'rgba(16, 185, 129, 0.8)' : 'rgba(255, 255, 255, 0.2)';
                ctx.beginPath();
                ctx.roundRect(-45, yPos + 12, 21, 21, 6);
                ctx.fill();

                // Text lines
                ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
                ctx.roundRect(-15, yPos + 16, 60, 5, 2);
                ctx.roundRect(-15, yPos + 26, 40, 5, 2);
                ctx.fill();
            }

            ctx.restore(); // End screen clip

            // Notch
            ctx.fillStyle = '#000000';
            ctx.beginPath();
            ctx.roundRect(-25, -136, 50, 14, [0, 0, 8, 8]);
            ctx.fill();

            // Floating Notification (Orbiting)
            const notifTime = time * 0.03;
            // Reverse orbit for RTL? No, orbit is universal
            const notifX = Math.cos(notifTime) * 90;
            const notifY = Math.sin(notifTime) * 40 - 20;

            ctx.translate(notifX, notifY);

            // Notification Box
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.shadowColor = 'rgba(0,0,0,0.1)';
            ctx.shadowBlur = 10;
            ctx.beginPath();
            ctx.roundRect(-25, -8, 50, 16, 8);
            ctx.fill();
            ctx.shadowBlur = 0;

            // Icon
            ctx.fillStyle = '#10B981';
            ctx.beginPath();
            ctx.arc(-15, 0, 4, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        };

        const animate = () => {
            time++;
            const width = canvas.width / (window.devicePixelRatio || 1);
            const height = canvas.height / (window.devicePixelRatio || 1);

            ctx.clearRect(0, 0, width, height);

            const targetX = mouseRef.current.x;
            const targetY = mouseRef.current.y;

            const centerX = width * 0.5;
            const centerY = height * 0.5;

            // Draw Background Particles
            particles.forEach(p => {
                p.y -= p.speed * (isHoveredRef.current ? 1.5 : 1);
                if (p.y < 0) {
                    p.y = height + 10;
                    p.x = Math.random() * width;
                }

                const parallaxX = targetX * p.depth * 30;
                const parallaxY = targetY * p.depth * 30;

                ctx.beginPath();
                ctx.arc(p.x + parallaxX, p.y + parallaxY, p.size * p.depth, 0, Math.PI * 2);

                ctx.fillStyle = `rgba(52, 211, 153, ${p.opacity * p.depth})`;
                ctx.fill();
            });

            // Draw Orbiting Rings
            ctx.save();
            ctx.translate(centerX, centerY);

            ctx.translate(targetX * 10, targetY * 10);

            const ringCount = 3;
            for (let i = 0; i < ringCount; i++) {
                ctx.rotate((time * 0.002) * (i % 2 === 0 ? 1 : -1));

                const radius = 160 + (i * 60);

                ctx.beginPath();
                ctx.arc(0, 0, radius, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255, 255, 255, 0.02)`;
                ctx.fill();

                ctx.strokeStyle = `rgba(16, 185, 129, ${0.1 - (i * 0.02)})`;
                ctx.lineWidth = 1;
                ctx.setLineDash([20, 40 + (i * 10)]);
                ctx.stroke();

                const satelliteAngle = time * (0.01 - (i * 0.002));
                const satX = Math.cos(satelliteAngle) * radius;
                const satY = Math.sin(satelliteAngle) * radius;

                ctx.beginPath();
                ctx.arc(satX, satY, 3, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(16, 185, 129, 0.6)`;
                ctx.fill();
            }

            ctx.restore();

            // Draw Phone Centered
            const floatOffset = Math.sin(time * 0.03) * 8;
            drawPhone(centerX, centerY + floatOffset, 0.85, targetX, targetY);

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            if (container) {
                container.removeEventListener('mousemove', handleMouseMove);
                container.removeEventListener('mouseenter', handleMouseEnter);
                container.removeEventListener('mouseleave', handleMouseLeave);
            }
            cancelAnimationFrame(animationFrameId);
        };
    }, [isRTL]); // Re-run if direction changes

    return (
        <section className="py-24 px-4 md:px-0" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="container mx-auto">
                <motion.div
                    ref={containerRef}
                    className="group relative w-full overflow-hidden bg-gray-900 rounded-[2.5rem] shadow-2xl border border-gray-800"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
                >
                    {/* Background Gradient */}
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-900/40 via-gray-900 to-gray-900 pointer-events-none" />

                    {/* Noise Texture */}
                    <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.15] mix-blend-overlay"
                        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22 opacity=%221%22/%3E%3C/svg%3E")' }}>
                    </div>

                    <div className="flex flex-col md:flex-row min-h-[550px] relative z-10">

                        {/* Content Side: 65% width on desktop */}
                        <div className="w-full md:w-[65%] p-10 md:p-20 flex flex-col justify-center items-start text-left order-1">

                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                <span className="inline-flex items-center gap-2 px-4 py-2 mb-8 text-xs font-bold tracking-widest text-emerald-400 uppercase bg-emerald-500/10 rounded-full border border-emerald-500/20 backdrop-blur-md">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                    {t('comingSoon.badge')}
                                </span>
                            </motion.div>

                            <motion.h2
                                className="text-4xl md:text-6xl font-extrabold text-white mb-8 leading-[1.1] tracking-tight max-w-2xl rtl:text-right"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                <Trans
                                    i18nKey="comingSoon.title"
                                    components={{
                                        1: <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300" />
                                    }}
                                />
                            </motion.h2>

                            <motion.p
                                className="text-lg md:text-xl text-gray-400 mb-12 max-w-lg leading-relaxed font-light rtl:text-right"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                            >
                                {t('comingSoon.description')}
                            </motion.p>

                            <motion.div
                                className="flex flex-wrap gap-5"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                            >
                                <button className="group flex items-center px-6 py-3.5 bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700 hover:border-gray-600 rounded-xl transition-all duration-300 backdrop-blur-sm">
                                    <svg className="w-8 h-8 text-white mr-3 shrink-0 rtl:ml-3 rtl:mr-0" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17.523 15.3414C17.514 15.3582 17.4877 15.4057 17.4435 15.4807C17.2023 15.8647 16.5592 16.8906 15.6565 18.2393C14.7712 19.5636 13.9317 20.8143 12.5539 20.8143C12.5348 20.8143 12.5186 20.8143 12.5029 20.8153C11.8596 20.8526 11.2612 20.6277 10.7093 20.4208C10.2798 20.2599 9.89735 20.1164 9.48914 20.1164C8.98661 20.1164 8.52841 20.2885 8.04502 20.4701C7.57508 20.6465 7.0984 20.8256 6.57793 20.8256C5.10186 20.8256 3.90382 19.1672 3.01851 17.8444C2.08643 16.4509 1.40938 14.524 1.40938 12.6373C1.40938 10.2248 2.87163 8.35859 4.96058 8.16345C5.00632 8.15865 5.05063 8.15615 5.09348 8.15615C5.97579 8.15615 6.78248 8.50348 7.42036 8.77799C7.81841 8.94935 8.16669 9.09916 8.44105 9.09916C8.71887 9.09916 9.099 8.93175 9.53036 8.74797C10.1989 8.4633 11.0805 8.08781 12.1158 8.08781C12.4205 8.08781 12.7231 8.1068 13.0152 8.14365C12.1824 8.77508 11.562 9.68074 11.562 10.7424C11.562 13.0135 13.3444 14.3548 13.9749 14.7352C13.9455 14.8147 13.9113 14.8975 13.8732 14.9806C13.6845 15.394 13.4691 15.8647 13.2458 16.3533C12.9642 16.9696 12.6738 17.6053 12.3364 18.1764C12.3359 18.1774 12.3352 18.1782 12.3347 18.1793L17.523 15.3414ZM11.9686 6.32422C11.867 5.16301 12.4468 4.09339 13.0076 3.29828C13.5658 2.50742 14.4988 1.90799 15.4219 1.98651C15.5469 3.26629 14.9392 4.34354 14.3535 5.10515C13.79 5.83705 12.7937 6.47178 11.9686 6.32422Z" />
                                    </svg>
                                    <div className="text-left rtl:text-right">
                                        <div className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">{t('comingSoon.availableOn')}</div>
                                        <div className="text-sm font-bold text-white leading-none mt-1">{t('comingSoon.appStore')}</div>
                                    </div>
                                </button>

                                <button className="group flex items-center px-6 py-3.5 bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700 hover:border-gray-600 rounded-xl transition-all duration-300 backdrop-blur-sm">
                                    <svg className="w-8 h-8 text-white mr-3 shrink-0 rtl:ml-3 rtl:mr-0" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M3.60938 13.0312C3.60938 13.0312 3.60938 13.0313 3.60938 13.0312L3.61201 13.0471L7.20059 19.9881L8.1654 21.8542L8.16853 21.848C9.53037 24.5385 13.2796 24.5385 14.6416 21.848L14.6367 21.8576L15.6015 19.9915L19.19 13.0506C19.19 13.0506 19.19 13.0505 19.19 13.0505C19.7828 11.9022 19.4623 10.4934 18.4326 9.77443C17.9366 9.42841 17.3888 9.38725 16.8906 9.43144L14.6738 5.17647L16.2081 2.21989C16.3218 2.00063 16.2367 1.73173 16.0177 1.61803C15.7989 1.50426 15.5298 1.58913 15.4162 1.80806L13.8443 4.83664C12.836 4.3814 11.7516 4.19502 10.7417 4.29858L9.70425 2.30604C9.58957 2.08643 9.31976 2.00282 9.1005 2.11718C8.88124 2.23154 8.79796 2.5008 8.91264 2.72041L11.0267 6.78248C10.5188 6.73299 9.96024 6.76813 9.45266 7.12241C8.42301 7.84134 8.10243 9.25016 8.69532 10.3985L3.60938 13.0312Z" />
                                    </svg>
                                    <div className="text-left rtl:text-right">
                                        <div className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">{t('comingSoon.availableOn')}</div>
                                        <div className="text-sm font-bold text-white leading-none mt-1">{t('comingSoon.googlePlay')}</div>
                                    </div>
                                </button>
                            </motion.div>
                        </div>

                        {/* Canvas Side: 35% width, centered content */}
                        <div className="w-full md:w-[35%] h-[400px] md:h-auto relative order-2">
                            <canvas
                                ref={canvasRef}
                                className="absolute inset-0 w-full h-full"
                            />
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default ComingSoonMobileApp;
